import {
  type FC, useCallback,
} from 'react';
import type { BoundaryTerritoryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  useBoundaryGroupPermissions, useBoundaryGroupSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useBoundaryFillSettingsPropsVariants } from '../../../../../boundary/settings/useBoundaryFillSettingsPropsVariants';
import { BoundaryFillSettingsContentComponent } from './boundaryFillSettingsContentComponent';

type BoundarySettingsContentContainerProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  onBoundaryTerritoryGroupChange: (boundaryTerritoryGroup: BoundaryTerritoryGroup) => void;
  valueIsInitial: boolean;
  isValid: boolean;
  onFormValidityChange: (btgId: BoundaryTerritoryGroupId, isValid: boolean) => void;
  onIsLoadingChange: (isLoading: boolean) => void;
}>;

export const BoundaryFillSettingsContentContainer: FC<BoundarySettingsContentContainerProps> = ({
  boundaryTerritoryGroup,
  onBoundaryTerritoryGroupChange,
  valueIsInitial,
  isValid,
  onFormValidityChange,
  onIsLoadingChange,
}) => {

  const boundaryGroup = useBoundaryGroupSelector(boundaryTerritoryGroup.boundaryGroupId);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup.boundaryGroupId);

  const updateFormValidity = useCallback((isValid: boolean) => {
    onFormValidityChange(boundaryTerritoryGroup.boundaryTerritoryGroupId, isValid);
  }, [boundaryTerritoryGroup.boundaryTerritoryGroupId, onFormValidityChange]);

  const boundaryTerritoryGroupProps = useBoundaryFillSettingsPropsVariants({
    boundaryTerritoryGroup,
    onBoundaryTerritoryGroupUpdate: onBoundaryTerritoryGroupChange,
    onIsLoadingChange,
  });

  if (!boundaryTerritoryGroupProps || !boundaryGroup || !boundaryPermissions) {
    return null;
  }

  return (
    <BoundaryFillSettingsContentComponent
      boundaryGroup={boundaryGroup}
      boundaryTerritoryGroup={boundaryTerritoryGroup}
      boundaryPermissions={boundaryPermissions}
      valueIsInitial={valueIsInitial}
      onFormValidityChange={updateFormValidity}
      onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
      isValid={isValid}
      {...boundaryTerritoryGroupProps}
    />
  );
};
